import { useMemo } from 'react';
import classnames from 'classnames';
import { BiSolidCheckCircle } from 'react-icons/bi';
import { MdOutlineWarning } from 'react-icons/md';
import { GROUND_TRIP_CHECKPOINT_TYPE } from '@lp/features/RoutePlanner/config';
import { getTripLocationTitle, validateCheckpointTime } from '@lp/features/RoutePlanner/utils';
import { parseZonedDateTime } from '@internationalized/date';
import { MS_IN_S } from '@/constants/date.constants';
import {
  CheckpointModeType,
  CheckpointTypeEnum,
  GroundTripCheckpointType,
  GroundTripPassenger,
} from '@/api/apis/drive-service';
import { convertMsToTimeString, formatDate } from '@/utils/helpers';
import { TripPassengers } from './TripPassengers';
import { TripComment } from './TripComment';

type StyleType = {
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  textStyle: string;
};

const DEFAULT_CHECKPOINT_STYLE: StyleType = {
  borderColor: 'border-base-5',
  backgroundColor: 'bg-base-5',
  textColor: 'text-white',
  textStyle: 'text-s-sb',
};

const CheckpointTypeStyles: Record<CheckpointModeType, StyleType> = {
  [CheckpointModeType.PickUp]: DEFAULT_CHECKPOINT_STYLE,
  [CheckpointModeType.DropOff]: DEFAULT_CHECKPOINT_STYLE,
  [CheckpointModeType.Wait]: {
    borderColor: 'border-[#191C20]',
    backgroundColor: 'bg-white',
    textColor: 'text-[#191C20]',
    textStyle: 'text-s-r',
  },
};

export type TripCheckpointProps = {
  data: GroundTripCheckpointType;
  tripPassengers: GroundTripPassenger[];
  departureDateTime: string;
  arrivalDateTime: string;
  isMergedTrip?: boolean;
};

export const TripCheckpoint = ({
  data,
  tripPassengers,
  departureDateTime,
  arrivalDateTime,
  isMergedTrip = false,
}: TripCheckpointProps) => {
  const isPlannedCheckpoint = data.$type === CheckpointTypeEnum.PlannedCheckpointResponse;
  const { borderColor, textColor, textStyle, backgroundColor } = CheckpointTypeStyles[data.modeData.mode];

  const passengersIds = useMemo(() => {
    switch (data.modeData.mode) {
      case CheckpointModeType.DropOff:
        return data.modeData.passengerIdsToDrop;
      case CheckpointModeType.PickUp:
        return data.modeData.passengerIdsToPickUp;
      default:
        return [];
    }
  }, [data]);

  const checkpointDateTimeString = useMemo(() => {
    const formatString = 'MMM dd, HH:mm zzz';

    switch (data.modeData.mode) {
      case CheckpointModeType.DropOff:
        return formatDate(data.modeData.dropOffDateTime, formatString, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
      case CheckpointModeType.PickUp:
        return formatDate(data.modeData.pickUpDateTime, formatString, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
      default:
        const dateTimeString = formatDate(data.modeData.waitStartTime, formatString, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
        const waitTime = convertMsToTimeString(data.modeData.waitDurationInSeconds * MS_IN_S);

        return `${dateTimeString}, ${waitTime}`;
    }
  }, [data]);

  const showDateTimeWarningIcon = useMemo(() => {
    if (isMergedTrip) return false;

    const departureDateTimeStringUTC = parseZonedDateTime(departureDateTime).toAbsoluteString();
    const arrivalDateTimeStringUTC = parseZonedDateTime(arrivalDateTime).toAbsoluteString();

    switch (data.modeData.mode) {
      case CheckpointModeType.DropOff:
        const isValidDropOff = validateCheckpointTime(
          { departureTime: departureDateTimeStringUTC, arrivalTime: arrivalDateTimeStringUTC },
          data.modeData.dropOffDateTime,
        );

        return !isValidDropOff;
      case CheckpointModeType.PickUp:
        const isValidPickUp = validateCheckpointTime(
          { departureTime: departureDateTimeStringUTC, arrivalTime: arrivalDateTimeStringUTC },
          data.modeData.pickUpDateTime,
        );

        return !isValidPickUp;
      default:
        return false;
    }
  }, [data.modeData, departureDateTime, arrivalDateTime, isMergedTrip]);

  return (
    <div
      className={classnames(
        'relative w-full pl-4 pr-13 py-2 rounded-lg',
        data.isCompleted ? 'bg-base-70' : 'bg-base-80',
      )}
    >
      <div className={classnames('w-full flex gap-3', { 'opacity-70': data.isCompleted })}>
        <div className="flex items-center gap-3">
          <div
            className={classnames(
              'shrink-0 w-6 h-6 flex justify-center items-center border-1 rounded-full',
              borderColor,
              textColor,
              textStyle,
              backgroundColor,
            )}
          >
            {GROUND_TRIP_CHECKPOINT_TYPE[data.modeData.mode]}
          </div>
        </div>
        <div className="flex flex-col gap-1 grow justify-center min-w-0">
          <span className="text-s-sb text-base-5 truncate">{getTripLocationTitle(data)}</span>
          {isPlannedCheckpoint && <TripPassengers tripPassengers={tripPassengers} ids={passengersIds} />}
          {isPlannedCheckpoint && <TripComment comment={data.comment} />}
        </div>
        <div className="flex gap-2 items-center justify-end shrink-0">
          {showDateTimeWarningIcon && (
            <div className="text-warning-30">
              <MdOutlineWarning size={16} />
            </div>
          )}
          <span className="text-s-sb text-base-5">{checkpointDateTimeString}</span>
        </div>
      </div>
      {data.isCompleted && (
        <div className="absolute top-0 h-full right-4 left-auto flex items-center text-[#0F8B99]">
          <BiSolidCheckCircle size={16} />
        </div>
      )}
    </div>
  );
};
